import { createSelector } from 'reselect';

import { fromChartSelectors } from '@mode/charts/domain';
import { EncodingHelpers, SwitchRenderModel, SwitchSchemaModel, makeSummaryData } from '@mode/charts/util-vegas';
import { FLAMINGO_RESERVED_REFS, generateUUID } from '@mode/shared/util-js';
import { routerSelector } from '~/reducers/selectors';

import { AppState, ChartState } from '@mode/legacy/types-app-state';
import { DataViewResponseTypes } from '@mode/shared/contract-common';
import { fromFlamingoSelectors, fromSwitchViewSelectors } from '@mode/switch-views/domain';
import { getChartState, getEncodedChartInNormalizedChartArray } from '../lib/chart-helpers';
import { ActiveExplorationSelectors } from './active-exploration.selectors';
import { NotebookSelectors } from './notebook.selectors';
import { SelectedReportSelectors } from './selected-report.selectors';

/* eslint-disable */
export namespace ExplorerSelectors {
  export const getToken = (state: AppState) => state.transient.explorer.chartToken;
  export const getQueryToken = fromChartSelectors.createQueryTokenSelector(FLAMINGO_RESERVED_REFS.ChartExplorer);
  export const getSwitchViewToken = fromChartSelectors.createSwitchViewTokenSelector(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );
  export const getEncodedChart = fromChartSelectors.createChartSnapshotSelector(FLAMINGO_RESERVED_REFS.ChartExplorer);

  export const getChartForFlamingoPlan = createSelector(
    (state: AppState) => state.transient.explorer.chartToken,
    fromChartSelectors.createChartTypeSelector(FLAMINGO_RESERVED_REFS.ChartExplorer),
    fromChartSelectors.createEncodingSelector(FLAMINGO_RESERVED_REFS.ChartExplorer),
    fromChartSelectors.createQueryTokenSelector(FLAMINGO_RESERVED_REFS.ChartExplorer),
    (token, chartType, encoding, queryToken) => ({
      queryToken,
      chartType,
      token,
      encoding,
    })
  );

  // Delay opening the modal until the model is loaded, useful for first load on a chart explore url
  export const getModalIsOpen = createSelector(
    (state: AppState) => state.transient.explorer.isOpen,
    fromSwitchViewSelectors.getModel,
    (isOpen, modelState) => modelState != null && isOpen
  );

  // @TODO: (@anthonysimone) [GF] temporary selector for GF flow, this is used in the explorer/designer paging flow, doesn't seem like
  // it should make a differennce that the model isn't considered for this purpose, but not 100%
  export const getModalIsOpenState = (state: AppState) => state.transient.explorer.isOpen;

  export const getModalIsFocused = (state: AppState) => state.transient.explorer.isFocused;

  export const getChart = createSelector(
    fromChartSelectors.selectChartState,
    fromSwitchViewSelectors.getModel,
    (charts, model): ChartState => {
      const chart = getEncodedChartInNormalizedChartArray(charts, FLAMINGO_RESERVED_REFS.ChartExplorer);
      if (chart && charts.tokens.has(FLAMINGO_RESERVED_REFS.ChartExplorer)) {
        return getChartState(chart, model, chart.queryToken);
      }
    }
  );

  // GF version of getChartState for the explorer
  export const getChartStateGf = createSelector(
    fromChartSelectors.selectChartState,
    fromSwitchViewSelectors.getModel,
    (charts, model): ChartState => {
      const chart = getEncodedChartInNormalizedChartArray(charts, FLAMINGO_RESERVED_REFS.ChartExplorer);
      if (chart && charts.tokens.has(FLAMINGO_RESERVED_REFS.ChartExplorer)) {
        return getChartState(chart, model, chart.queryToken);
      }
    }
  );

  export const getModelForSelectedChart = createSelector(
    SelectedReportSelectors.getChartModels,
    getChart,
    (models, chart) => (chart ? models.getRecord(chart.queryToken) : undefined)
  );

  export const getSelectedChartType = createSelector(getChart, (chart) => (chart ? chart.chartType : null));
  // export const getSelectedChartRelName = createSelector(getChart, (chart) => chart ? chart.queryToken : null);
  export const getSelectedChartPaletteToken = createSelector(getChart, (chart) =>
    chart ? chart.colorPaletteToken : null
  );
  export const getSelectedChartTitle = createSelector(getChart, (chart) => (chart ? chart.title : null));
  export const getSelectedChartEncoding = createSelector(
    getChart,
    (chart) => (chart ? chart.encResult : null) && chart.encResult.encoding
  );
  export const getSelectedChartFormat = fromChartSelectors.createChartFormatSelector(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );
  export const getSelectedChartFieldFormatMap = fromChartSelectors.createChartFieldFormatMapSelector(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );
  export const getSelectedChartFieldColorMap = fromChartSelectors.createChartFieldColorMapSelector(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );
  export const getSelectedChartFacetSizeMap = fromChartSelectors.createChartFacetSizeMapSelector(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );
  export const getSelectedChartIsAutoUpdateDisabled = fromChartSelectors.createIsAutoUpdateDisabledSelector(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );
  export const getSelectedChartManuallyApplyChanges = fromChartSelectors.createManuallyApplyChangesSelector(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );
  export const getSelectedChartUnappliedChanges = fromChartSelectors.createUnappliedChangesSelector(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );
  export const getSelectedChartCaption = fromChartSelectors.createCaptionSelector(FLAMINGO_RESERVED_REFS.ChartExplorer);

  export const getViewVegas = createSelector(
    getSelectedChartType,
    getSelectedChartFormat,
    getSelectedChartEncoding,
    getSelectedChartTitle,
    getSelectedChartFieldFormatMap,
    getSelectedChartFieldColorMap,
    getSelectedChartFacetSizeMap,
    getSelectedChartIsAutoUpdateDisabled,
    getSelectedChartCaption,
    (chartType, format, encoding, title, fieldFormats, fieldColors, facetSizes, isAutoUpdateDisabled, caption) => ({
      chartType,
      format,
      encoding,
      title,
      fieldFormats,
      fieldColors,
      facetSizes,
      isAutoUpdateDisabled,
      caption,
    })
  );

  export const getChartAsExploration = createSelector(
    getEncodedChart,
    getQueryToken,
    (chart, queryToken): DataViewResponseTypes.Exploration => ({
      query: queryToken,
      chart: {
        view_vegas: {
          chartType: chart.chartType,
          format: chart.format,
          fieldFormats: chart.fieldFormats,
          fieldColors: chart.fieldColors,
          facetSizes: chart.facetSizes,
          encoding: chart.encoding,
          title: chart.title,
          description: chart.description,
          caption: chart.caption,
          isAutoUpdateDisabled: chart.isAutoUpdateDisabled ?? false,
        },
        view_version: 2,
        color_palette_token: chart.colorPaletteToken,
      },
      name: `Exploration of ${chart.title || 'Untitled'}`,
    })
  );

  export const title = createSelector(
    routerSelector,
    NotebookSelectors.vizTitlesArray,
    fromChartSelectors.selectResources,
    ActiveExplorationSelectors.getTitle,
    getChartAsExploration,
    (routerState, notebookTitles, legacyResource, explorationName, exploreRouteTitle): String => {
      let id = routerState.params.vizId || routerState.params.explorationId;

      /* In memory exploration */
      if (
        routerState.name === 'reportShow.noRuns.chart.explore' ||
        routerState.name === 'reportShow.runs.chart.explore'
      ) {
        return exploreRouteTitle.name;
      }

      /* This is a notebook */
      if (notebookTitles.has(id)) {
        return `Viewing ${notebookTitles.get(id) || 'untitled visualization'}`;
      }

      /* This is a legacy chart */
      if (legacyResource.has(id) && legacyResource.get(id).view.chartTitle) {
        return `Viewing ${legacyResource.get(id).view.chartTitle}`;
      }

      /* This is an exploration */
      if (id !== undefined) {
        return explorationName;
      }

      return null;
    }
  );

  export const getIsValid = createSelector(
    getSelectedChartType,
    getSelectedChartEncoding,
    getQueryToken,
    fromSwitchViewSelectors.getModel,
    (type, encoding, queryToken, model) => {
      const schemaModel = SwitchSchemaModel.make(model, queryToken);
      const isValid =
        type === 'table'
          ? EncodingHelpers.validateTableEncodingFields(encoding, schemaModel).length <= 0
          : EncodingHelpers.validateChartEncodingFields(encoding, schemaModel).length <= 0;

      return isValid;
    }
  );

  export const getIsComplete = createSelector(getSelectedChartType, getSelectedChartEncoding, (type, encoding) => {
    return EncodingHelpers.isChartEncodingComplete(encoding, type);
  });

  export const getIsAutoOrManualUpdate = createSelector(
    getSelectedChartIsAutoUpdateDisabled,
    getSelectedChartManuallyApplyChanges,
    (isAutoUpdateDisabled, manuallyApplyChanges) => !isAutoUpdateDisabled || manuallyApplyChanges
  );

  /**
   * Get values from exploration to pass to Segment analytics
   * TODO: Jay - selectNoEmpty use here? Optimize
   */
  export const explorationAnalyticsProps = createSelector(getChart, getToken, (chart, token) => {
    return (
      chart && {
        event_id: generateUUID(),
        product_context: 'report',
        sub_context: 'exploration',
        chart_id: `chart_${token}`,
        chart_type: chart.chartType,
      }
    );
  });

  export function getFlamingoChartSummaryData(planId: string) {
    return createSelector(
      getChart,
      fromSwitchViewSelectors.getModel,
      fromFlamingoSelectors.createPlanSelector(planId),
      fromFlamingoSelectors.createResultSelector(planId),
      (chart, model, plan, result) => {
        if (chart != null) {
          const { encResult, chartType, queryToken, format } = chart;
          if (chartType !== 'table') {
            const { encoding } = encResult;

            const isValid =
              EncodingHelpers.isChartEncodingComplete(encoding, chartType) &&
              EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
                0;

            if (isValid) {
              if (model != null && queryToken != null && plan != null && result != null) {
                const renderModel = SwitchRenderModel.make(model, queryToken, plan, result);
                return makeSummaryData(renderModel, encoding);
              }
            }
          }
        }

        return null;
      }
    );
  }
}
